import "./App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "./components/ui/toaster";

// Eager load Home page for faster initial load
import Home from "./pages/Home";

// Design system preview (internal)
const DesignSystem = lazy(() => import("./pages/DesignSystem"));

// Phase 2 — New product pages
const C4PStandard = lazy(() => import("./pages/C4PStandard"));
const C4PAdvanced = lazy(() => import("./pages/C4PAdvanced"));
const SAPB1CloudHostingHub = lazy(() => import("./pages/SAPB1CloudHostingHub"));

// Phase 3 & 4 — Architecture 4.0 + Migration Factory
const Architecture4 = lazy(() => import("./pages/Architecture4"));
const MigrationFactory = lazy(() => import("./pages/MigrationFactory"));

// Phase 6 — Supporting pages
const Testimonials = lazy(() => import("./pages/Testimonials"));
const Partners = lazy(() => import("./pages/Partners"));

// Lazy load all other pages for code splitting
const HomeOld = lazy(() => import("./pages/HomeOld"));

// Who We Are Pages
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ValueProposition = lazy(() => import("./pages/ValueProposition"));
const PartnerBenefits = lazy(() => import("./pages/PartnerBenefits"));
const PartnerOffering = lazy(() => import("./pages/PartnerOffering"));
const PlatformOverview = lazy(() => import("./pages/PlatformOverview"));
const SecurityCompliance = lazy(() => import("./pages/SecurityCompliance"));
const WebClientHosting = lazy(() => import("./pages/WebClientHosting"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const EcosystemMarketplace = lazy(() => import("./pages/EcosystemMarketplace"));
const VendorDetail = lazy(() => import("./pages/VendorDetail"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const OnboardingProcess = lazy(() => import("./pages/OnboardingProcess"));
const ProjectManagement = lazy(() => import("./pages/ProjectManagement"));
const CustomerSupport = lazy(() => import("./pages/CustomerSupport"));
const CloudMigrations = lazy(() => import("./pages/CloudMigrations"));
const Integrations = lazy(() => import("./pages/Integrations"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const BlogNews = lazy(() => import("./pages/BlogNews"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));

// Data Sovereignty Pages
const DataSovereignty = lazy(() => import("./pages/DataSovereignty"));
const DataSovereigntyUAE = lazy(() => import("./pages/DataSovereigntyUAE"));
const DataSovereigntyEurope = lazy(() => import("./pages/DataSovereigntyEurope"));
const DataSovereigntyUSA = lazy(() => import("./pages/DataSovereigntyUSA"));
const DataSovereigntyAfrica = lazy(() => import("./pages/DataSovereigntyAfrica"));
const DataSovereigntyCanada = lazy(() => import("./pages/DataSovereigntyCanada"));
const DataSovereigntySingapore = lazy(() => import("./pages/DataSovereigntySingapore"));

// SAP Business One Hosting Pages
const Dedicated = lazy(() => import("./pages/Dedicated"));
const MultiTenant = lazy(() => import("./pages/MultiTenant"));
const SingleTenant = lazy(() => import("./pages/SingleTenant"));
const MTPro = lazy(() => import("./pages/MTPro"));
const RackToRocket = lazy(() => import("./pages/RackToRocket"));

// Landing Pages
const LandingPageView = lazy(() => import("./pages/LandingPageView"));

// Other Cloud Hosting Pages
const BPAReady = lazy(() => import("./pages/BPAReady"));
const NewIntelligence = lazy(() => import("./pages/NewIntelligence"));
const MyWave = lazy(() => import("./pages/MyWave"));

// AWS Services Pages
const InfrastructureService = lazy(() => import("./pages/InfrastructureService"));
const DisasterRecovery = lazy(() => import("./pages/DisasterRecovery"));

// Partner Portal
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
const PortalLogin = lazy(() => import("./pages/PortalLogin"));
const PortalLayout = lazy(() => import("./components/PortalLayout"));
const Dashboard = lazy(() => import("./pages/Portal/Dashboard"));
const Assets = lazy(() => import("./pages/Portal/Assets"));
const QuoteBuilder = lazy(() => import("./pages/Portal/QuoteBuilder"));
const Profitability = lazy(() => import("./pages/Portal/Profitability"));

// Admin Pages
const UserManagement = lazy(() => import("./pages/Portal/Admin/UserManagement"));
const ProductManagement = lazy(() => import("./pages/Portal/Admin/ProductManagement"));
const AccessRequests = lazy(() => import("./pages/Portal/Admin/AccessRequests"));
const BlogManagement = lazy(() => import("./pages/Portal/Admin/BlogManagement"));
const FAQManagement = lazy(() => import("./pages/Portal/Admin/FAQManagement"));
const WebinarManagement = lazy(() => import("./pages/Portal/Admin/WebinarManagement"));
const WebinarRegistrationManagement = lazy(() => import("./pages/Portal/Admin/WebinarRegistrationManagement"));

// ResourcePrompts moved to Quote Tool
const Webinars = lazy(() => import("./pages/Webinars"));
const WebinarDetail = lazy(() => import("./pages/WebinarDetail"));
const LeadManagement = lazy(() => import("./pages/Portal/Admin/LeadManagement"));
const LandingPageManagement = lazy(() => import("./pages/Portal/Admin/LandingPageManagement"));
const VendorManagement = lazy(() => import("./pages/Portal/Admin/VendorManagement"));
const ContactManagement = lazy(() => import("./pages/Portal/Admin/ContactManagement"));
const MarketplaceSettings = lazy(() => import("./pages/Portal/Admin/MarketplaceSettings"));
const MarketplaceProductManagement = lazy(() => import("./pages/Portal/Admin/MarketplaceProductManagement"));
const SEOManagement = lazy(() => import("./pages/Portal/Admin/SEOManagement"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-slate-600">Loading...</p>
    </div>
  </div>
);

// ─── Route tracker: stores the last-visited page so the contact form
// can show where a lead came from. Must be inside <BrowserRouter>.
const SOURCE_LABELS = {
  '/': 'Homepage',
  '/c4p-standard': 'C4P Standard',
  '/c4p-advanced': 'C4P Advanced',
  '/mt-pro': 'C4P MTPro',
  '/architecture-4': 'Architecture 4.0',
  '/migration-factory': 'Migration Factory',
  '/sap-b1-cloud-hosting': 'SAP B1 Cloud Hosting Hub',
  '/partners': 'Partners',
  '/about-us': 'About Us',
  '/testimonials': 'Testimonials',
  '/webinars': 'Webinars',
  '/data-sovereignty': 'Data Sovereignty',
  '/data-sovereignty/singapore': 'Data Sovereignty — Singapore',
  '/rack-to-rocket': 'Rack to Rocket',
  '/value-proposition': 'Value Proposition',
  '/platform-overview': 'Platform Overview',
  '/security-compliance': 'Security & Compliance',
  '/customer-support': 'Customer Support',
  '/infrastructure-service': 'Infrastructure Service',
};

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname !== '/contact') {
      const label = SOURCE_LABELS[location.pathname] || 'Website';
      sessionStorage.setItem('c4p_source_path', location.pathname);
      sessionStorage.setItem('c4p_source_label', label);
      sessionStorage.setItem('c4p_source_ts', Date.now().toString());
    }
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <RouteTracker />
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Public Routes with Navbar */}
              <Route path="/" element={<><Navbar /><Home /><Footer /></>} />
            <Route path="/design-system" element={<><Navbar /><DesignSystem /><Footer /></>} />
            <Route path="/home-old" element={<><Navbar /><HomeOld /><Footer /></>} />
            <Route path="/about-us" element={<><Navbar /><AboutUs /><Footer /></>} />
            <Route path="/value-proposition" element={<><Navbar /><ValueProposition /><Footer /></>} />
            {/* /partner-benefits redirects to /partners — see 301 redirects section below */}
            <Route path="/partner-cloud-offering" element={<><Navbar /><PartnerOffering /><Footer /></>} />
            <Route path="/platform-overview" element={<><Navbar /><PlatformOverview /><Footer /></>} />
            <Route path="/security-compliance" element={<><Navbar /><SecurityCompliance /><Footer /></>} />
            <Route path="/onboarding-process" element={<><Navbar /><OnboardingProcess /><Footer /></>} />
            <Route path="/project-management" element={<><Navbar /><ProjectManagement /><Footer /></>} />
            <Route path="/customer-support" element={<><Navbar /><CustomerSupport /><Footer /></>} />
            <Route path="/cloud-migrations" element={<><Navbar /><CloudMigrations /><Footer /></>} />
            <Route path="/integrations" element={<><Navbar /><Integrations /><Footer /></>} />
            <Route path="/faqs" element={<><Navbar /><FAQPage /><Footer /></>} />
            <Route path="/contact" element={<><Navbar /><Contact /><Footer /></>} />
            <Route path="/privacy-policy" element={<><Navbar /><PrivacyPolicy /><Footer /></>} />
            <Route path="/terms-conditions" element={<><Navbar /><TermsConditions /><Footer /></>} />
            <Route path="/blog-news" element={<><Navbar /><BlogNews /><Footer /></>} />
            <Route path="/blog/:slug" element={<><Navbar /><BlogArticle /><Footer /></>} />
            <Route path="/bundle" element={<Navigate to="/sap-b1-cloud-hosting" replace />} />
            <Route path="/landing/bundle" element={<Navigate to="/sap-b1-cloud-hosting" replace />} />
            <Route path="/webinars" element={<><Navbar /><Webinars /><Footer /></>} />
            <Route path="/webinars/:slug" element={<><Navbar /><WebinarDetail /><Footer /></>} />
            <Route path="/data-sovereignty" element={<><Navbar /><DataSovereignty /><Footer /></>} />
            <Route path="/data-sovereignty/uae" element={<><Navbar /><DataSovereigntyUAE /><Footer /></>} />
            <Route path="/data-sovereignty/europe" element={<><Navbar /><DataSovereigntyEurope /><Footer /></>} />
            <Route path="/data-sovereignty/usa" element={<><Navbar /><DataSovereigntyUSA /><Footer /></>} />
            <Route path="/data-sovereignty/canada" element={<><Navbar /><DataSovereigntyCanada /><Footer /></>} />
            <Route path="/data-sovereignty/africa" element={<><Navbar /><DataSovereigntyAfrica /><Footer /></>} />
            <Route path="/data-sovereignty/singapore" element={<><Navbar /><DataSovereigntySingapore /><Footer /></>} />
            
            {/* Phase 2 — New product pages */}
            <Route path="/c4p-standard" element={<><Navbar /><C4PStandard /><Footer /></>} />
            <Route path="/c4p-advanced" element={<><Navbar /><C4PAdvanced /><Footer /></>} />
            <Route path="/sap-b1-cloud-hosting" element={<><Navbar /><SAPB1CloudHostingHub /><Footer /></>} />
            
            {/* Phase 3 & 4 — Architecture 4.0 + Migration Factory */}
            <Route path="/architecture-4" element={<><Navbar /><Architecture4 /><Footer /></>} />
            <Route path="/migration-factory" element={<><Navbar /><MigrationFactory /><Footer /></>} />
            
            {/* Phase 6 — Supporting pages */}
            <Route path="/testimonials" element={<><Navbar /><Testimonials /><Footer /></>} />
            <Route path="/partners" element={<><Navbar /><Partners /><Footer /></>} />
            
            {/* 301 redirects — legacy URLs → new pages */}
            <Route path="/dedicated" element={<Navigate to="/c4p-advanced" replace />} />
            <Route path="/multi-tenant" element={<Navigate to="/c4p-standard" replace />} />
            <Route path="/partner-benefits" element={<Navigate to="/partners" replace />} />
            
            {/* Keep originals accessible via old slug for any bookmarked links */}
            <Route path="/single-tenant" element={<><Navbar /><SingleTenant /><Footer /></>} />
            <Route path="/mt-pro" element={<><Navbar /><MTPro /><Footer /></>} />
            <Route path="/mtpro" element={<><Navbar /><MTPro /><Footer /></>} />
            <Route path="/rack-to-rocket" element={<><Navbar /><RackToRocket /><Footer /></>} />
            <Route path="/bpa-ready" element={<><Navbar /><BPAReady /><Footer /></>} />
            <Route path="/new-intelligence" element={<><Navbar /><NewIntelligence /><Footer /></>} />
            <Route path="/mywave" element={<><Navbar /><MyWave /><Footer /></>} />
            <Route path="/infrastructure-service" element={<><Navbar /><InfrastructureService /><Footer /></>} />
            <Route path="/disaster-recovery" element={<><Navbar /><DisasterRecovery /><Footer /></>} />
            
            {/* SAP B1 Hub Pages */}
            <Route path="/sap-b1/web-client-hosting" element={<><Navbar /><WebClientHosting /><Footer /></>} />
            <Route path="/sap-b1/marketplace" element={<><Navbar /><ComingSoon /><Footer /></>} />
            <Route path="/sap-b1/marketplace/vendor/:slug" element={<><Navbar /><VendorDetail /><Footer /></>} />
            <Route path="/sap-b1/marketplace/product/:slug" element={<><Navbar /><ProductDetail /><Footer /></>} />
            <Route path="/sap-b1/updates" element={<><Navbar /><ComingSoon /><Footer /></>} />
            <Route path="/sap-b1/whats-new" element={<><Navbar /><ComingSoon /><Footer /></>} />
            
            {/* Dynamic Landing Pages */}
            <Route path="/landing/:slug" element={<><Navbar /><LandingPageView /><Footer /></>} />
            
            {/* Custom Landing Page - Rack to Rocket (redirect from /landing/rack-to-rocket) */}
            <Route path="/landing/rack-to-rocket" element={<><Navbar /><RackToRocket /><Footer /></>} />
            
            {/* Portal Login (No Navbar) */}
            <Route path="/portal/login" element={<PortalLogin />} />
            
            {/* Protected Portal Routes */}
            <Route path="/portal" element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="assets" element={<Assets />} />
              <Route path="quote-builder" element={<QuoteBuilder />} />
              <Route path="profitability" element={<Profitability />} />
              {/* Admin Routes */}
              <Route path="admin/users" element={<UserManagement />} />
              <Route path="admin/products" element={<ProductManagement />} />
              <Route path="admin/access-requests" element={<AccessRequests />} />
              <Route path="admin/landing-pages" element={<LandingPageManagement />} />
              <Route path="admin/blog" element={<BlogManagement />} />
              <Route path="admin/webinars" element={<WebinarManagement />} />
              <Route path="admin/webinar-registrations" element={<WebinarRegistrationManagement />} />
              <Route path="admin/faqs" element={<FAQManagement />} />
              <Route path="admin/leads" element={<LeadManagement />} />
              <Route path="admin/seo" element={<SEOManagement />} />
              <Route path="admin/marketplace/vendors" element={<VendorManagement />} />
              <Route path="admin/marketplace/contacts" element={<ContactManagement />} />
              <Route path="admin/marketplace/products" element={<MarketplaceProductManagement />} />
              <Route path="admin/marketplace/settings" element={<MarketplaceSettings />} />
            </Route>
          </Routes>
          </Suspense>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
